/**
 * VOUCHEE API SERVICE - PRODUCTION VERSION
 *
 * All API calls to Vouchee Nexus Backend
 * NO MOCK DATA - Production ready
 */

import httpClient, { normalizeError } from "./httpClient";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const AUTH_TOKEN_KEY = "vouchee_auth_token";
const AUTH_USER_KEY = "vouchee_user";
const REFRESH_TOKEN_KEY = "vouchee_refresh_token";

const handleApiError = (error, fallbackMessage) => {
  const normalized = normalizeError(error);
  const nextError = new Error(normalized.message || fallbackMessage);
  // Preserve backend response details for UI error rendering.
  nextError.status = normalized.status;
  nextError.data = normalized.data;
  nextError.isOffline = normalized.isOffline;
  throw nextError;
};

const persistSession = (token, user, refreshToken) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
  if (user) {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

const clearSession = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const getStoredToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
export const getStoredRefreshToken = () =>
  localStorage.getItem(REFRESH_TOKEN_KEY);

export const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// ============================================================================
// AUTHENTICATION
// ============================================================================

export const signup = async ({
  email,
  password,
  full_name,
  role,
  accepted_terms,
  gdpr_consent,
  fingerprint,
  ref_code,
  ref_business,
}) => {
  try {
    console.log("[SIGNUP] Sending request:", {
      email,
      full_name,
      role,
      accepted_terms,
    });
    const data = await httpClient.post("/api/auth/signup", {
      email,
      password,
      full_name,
      role: role || "user",
      accepted_terms: accepted_terms !== undefined ? accepted_terms : true,
      gdpr_consent: gdpr_consent || false,
      fingerprint,
      ...(ref_code ? { ref_code } : {}),
      ...(ref_business ? { ref_business } : {}),
    });

    console.log("[SIGNUP] Response received:", {
      hasToken: !!data.access_token,
      hasUser: !!data.user,
      userId: data.user?.id,
      userEmail: data.user?.email,
    });

    persistSession(data.access_token, data.user, data.refresh_token);
    return { user: data.user, token: data.access_token };
  } catch (error) {
    console.error("[SIGNUP] Error:", error);
    handleApiError(error, "Failed to create account");
  }
};

export const login = async ({ email, password }) => {
  try {
    const formData = new URLSearchParams();
    formData.append("username", email);
    formData.append("password", password);

    const tokenResponse = await httpClient.post("/api/auth/token", formData, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    if (!tokenResponse.access_token) {
      throw new Error("No token received");
    }

    // Fetch full profile, but fall back to the user object from the token
    // response so login still succeeds if /profile/me is temporarily down.
    let user = tokenResponse.user || null;
    try {
      const profileResponse = await httpClient.get("/api/profile/me", {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
      });
      if (profileResponse && profileResponse.id) {
        user = profileResponse;
      }
    } catch {
      // Profile fetch failed — use token response user data as fallback
    }

    if (!user) {
      throw new Error("No user data received");
    }

    persistSession(
      tokenResponse.access_token,
      user,
      tokenResponse.refresh_token,
    );
    return { user, token: tokenResponse.access_token };
  } catch (error) {
    handleApiError(error, "Login failed. Check your credentials.");
  }
};

export const addRole = async (role) => {
  try {
    const data = await httpClient.post("/api/auth/add-role", { role });
    const currentUser = getCurrentUser() || {};
    const updatedUser = data.user ? { ...currentUser, ...data.user } : currentUser;
    persistSession(null, updatedUser, null);
    return { user: updatedUser, message: data.message };
  } catch (error) {
    handleApiError(error, "Failed to update account role");
  }
};

export const logout = async () => {
  // Revoke tokens server-side before clearing local storage
  try {
    const token = getStoredToken();
    const refreshToken = getStoredRefreshToken();
    if (token) {
      await httpClient.post("/api/auth/logout", {
        refresh_token: refreshToken || undefined,
      });
    }
  } catch {
    // Don't block logout if server call fails
  }
  clearSession();
  return { success: true };
};

export const resetPassword = async (email) => {
  try {
    await httpClient.post("/api/auth/forgot-password", { email });
    return { success: true };
  } catch (error) {
    handleApiError(error, "Failed to send reset email");
  }
};

// ============================================================================
// USER PROFILE
// ============================================================================

export const getUserProfile = async (_userId) => {
  try {
    const data = await httpClient.get("/api/profile/me");
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch user profile");
  }
};

export const updateUserProfile = async (updates) => {
  try {
    const data = await httpClient.put("/api/profile/me", updates);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to update profile");
  }
};

export const getProfileStats = async () => {
  try {
    const data = await httpClient.get("/api/profile/stats");
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch profile stats");
  }
};

// ============================================================================
// BUSINESSES
// ============================================================================

export const getBusinesses = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.search) params.append("search", filters.search);
    if (filters.category) params.append("category", filters.category);
    if (filters.location) params.append("location", filters.location);
    if (filters.minRating) params.append("min_rating", filters.minRating);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset) params.append("offset", filters.offset);

    const queryString = params.toString();
    const url = `/api/businesses/${queryString ? `?${queryString}` : ""}`;

    const data = await httpClient.get(url);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch businesses:", error);
    return [];
  }
};

export const getBusinessById = async (id) => {
  try {
    const data = await httpClient.get(`/api/businesses/${id}`);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch business details");
  }
};

export const getMyBusinesses = async (_userId) => {
  try {
    const data = await httpClient.get("/api/businesses/my");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch my businesses:", error);
    return [];
  }
};

export const createBusiness = async (businessData) => {
  try {
    const data = await httpClient.post("/api/businesses/manual", businessData);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to create business");
  }
};

export const updateBusiness = async (businessId, updates) => {
  try {
    const data = await httpClient.put(`/api/businesses/${businessId}`, updates);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to update business");
  }
};

export const deleteBusiness = async (businessId) => {
  try {
    await httpClient.delete(`/api/businesses/${businessId}`);
    return { success: true };
  } catch (error) {
    handleApiError(error, "Failed to delete business");
  }
};

// ============================================================================
// EARNINGS & REFERRALS
// ============================================================================

export const getEarnings = async (_userId) => {
  try {
    const data = await httpClient.get("/api/earnings");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch earnings:", error);
    return [];
  }
};

export const getEarningsSummary = async () => {
  try {
    const data = await httpClient.get("/api/earnings/summary");
    return data;
  } catch (error) {
    console.error("Failed to fetch earnings summary:", error);
    return {
      total_earned: 0,
      pending_earnings: 0,
      available_for_payout: 0,
      total_paid_out: 0,
    };
  }
};

export const getTransactions = async () => {
  try {
    const data = await httpClient.get("/api/earnings/transactions");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch transactions:", error);
    return [];
  }
};

export const getReferrals = async (_userId) => {
  try {
    // Get vouches (referrals) for the current user
    const data = await httpClient.get("/api/profile/vouches/my-vouches");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch referrals:", error);
    return [];
  }
};

// ============================================================================
// VOUCHES
// ============================================================================

export const getMyVouches = async () => {
  try {
    const data = await httpClient.get("/api/profile/vouches/my-vouches");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch my vouches:", error);
    return [];
  }
};

export const getBusinessVouches = async (businessId) => {
  try {
    const data = await httpClient.get(`/api/businesses/${businessId}/vouches`);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch business vouches:", error);
    return [];
  }
};

// ============================================================================
// BADGES & ACHIEVEMENTS
// ============================================================================

export const getBadges = async (_userId) => {
  try {
    // Fetch user stats which includes badge information
    const stats = await httpClient.get("/api/profile/stats");

    // Transform stats into badges format
    const badges = [];

    if (stats.total_vouches >= 1) {
      badges.push({
        id: "first_vouch",
        name: "First Vouch",
        description: "Created your first vouch",
        icon: "🎉",
        earned: true,
        earnedAt: new Date().toISOString(),
      });
    }

    if (stats.total_vouches >= 10) {
      badges.push({
        id: "vouch_veteran",
        name: "Vouch Veteran",
        description: "Created 10 vouches",
        icon: "⭐",
        earned: true,
        earnedAt: new Date().toISOString(),
      });
    }

    if (stats.total_earnings > 0) {
      badges.push({
        id: "first_earning",
        name: "First Earning",
        description: "Earned your first commission",
        icon: "💰",
        earned: true,
        earnedAt: new Date().toISOString(),
      });
    }

    return badges;
  } catch (error) {
    console.error("Failed to fetch badges:", error);
    return [];
  }
};

// ============================================================================
// DASHBOARD DATA
// ============================================================================

export const getAdvocateDashboard = async (_userId) => {
  try {
    const [profile, stats, earnings, vouches] = await Promise.all([
      httpClient.get("/api/profile/me"),
      httpClient.get("/api/profile/stats"),
      httpClient.get("/api/earnings/summary"),
      httpClient.get("/api/profile/vouches/my-vouches"),
    ]);

    return {
      profile,
      stats: {
        totalVouches: stats.total_vouches || 0,
        totalEarnings: earnings.total_earned || 0,
        pendingEarnings: earnings.pending_earnings || 0,
        activeBusinesses: stats.active_businesses || 0,
      },
      recentVouches: Array.isArray(vouches) ? vouches.slice(0, 5) : [],
      earnings: {
        total: earnings.total_earned || 0,
        pending: earnings.pending_earnings || 0,
        available: earnings.available_for_payout || 0,
      },
    };
  } catch (error) {
    console.error("Failed to fetch advocate dashboard:", error);
    throw error;
  }
};

export const getBusinessDashboard = async (businessId) => {
  try {
    const [business, vouches, earnings, analytics] = await Promise.all([
      httpClient.get(`/api/businesses/${businessId}`),
      httpClient.get(`/api/businesses/${businessId}/vouches`),
      httpClient.get(`/api/businesses/${businessId}/earnings`),
      httpClient
        .get(`/api/businesses/${businessId}/analytics`)
        .catch(() => null),
    ]);

    return {
      business,
      stats: {
        totalVouches: Array.isArray(vouches) ? vouches.length : 0,
        totalRevenue: earnings?.total_revenue || 0,
        activeAdvocates: earnings?.active_advocates || 0,
        conversionRate: analytics?.conversion_rate || 0,
      },
      recentVouches: Array.isArray(vouches) ? vouches.slice(0, 10) : [],
      earnings: earnings || { total_revenue: 0, total_commissions_paid: 0 },
      analytics: analytics || {},
    };
  } catch (error) {
    console.error("Failed to fetch business dashboard:", error);
    throw error;
  }
};

// ============================================================================
// SEARCH & DISCOVERY
// ============================================================================

export const searchBusinesses = async (query) => {
  try {
    const data = await httpClient.get(
      `/api/search/businesses?q=${encodeURIComponent(query)}`,
    );
    return Array.isArray(data) ? data : data?.businesses || [];
  } catch (error) {
    console.error("Failed to search businesses:", error);
    return [];
  }
};

// ============================================================================
// NOTIFICATIONS
// ============================================================================

export const getNotifications = async () => {
  try {
    const data = await httpClient.get("/api/notifications");
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to fetch notifications:", error);
    return [];
  }
};

export const markNotificationRead = async (notificationId) => {
  try {
    await httpClient.put(`/api/notifications/${notificationId}/read`);
    return { success: true };
  } catch (error) {
    console.error("Failed to mark notification as read:", error);
    return { success: false };
  }
};

// ============================================================================
// PARTNERSHIPS
// ============================================================================

export const getPartnershipStatus = async () => {
  try {
    const data = await httpClient.get("/api/partnerships/status");
    return data;
  } catch (error) {
    console.error("Failed to fetch partnership status:", error);
    return {
      has_custom_partnerships: false,
      pending_invites: 0,
      active_partnerships_count: 0,
    };
  }
};

// ============================================================================
// ADMIN
// ============================================================================

export const getPlatformSettings = async () => {
  try {
    const data = await httpClient.get("/api/admin/platform-settings");
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch platform settings");
  }
};

export const updatePlatformSettings = async (settings) => {
  try {
    const data = await httpClient.put("/api/admin/platform-settings", settings);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to update platform settings");
  }
};

export const getAdminAnalytics = async () => {
  try {
    const data = await httpClient.get("/api/admin/stats");
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch admin analytics");
  }
};

// ============================================================================
// RATINGS & REVIEWS (Two-Way: Rate Business + Rate Vouchee)
// ============================================================================

export const submitRating = async (ratingData) => {
  try {
    const data = await httpClient.post("/api/ratings", ratingData);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to submit rating");
  }
};

export const getBusinessRatings = async (businessId, page = 1, limit = 20) => {
  try {
    const data = await httpClient.get(
      `/api/ratings/business/${businessId}?page=${page}&limit=${limit}`,
    );
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch business ratings");
  }
};

export const getVoucheeRatings = async (voucheeId, page = 1, limit = 20) => {
  try {
    const data = await httpClient.get(
      `/api/ratings/vouchee/${voucheeId}?page=${page}&limit=${limit}`,
    );
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch vouchee ratings");
  }
};

export const respondToRating = async (ratingId, responseText) => {
  try {
    const data = await httpClient.post(`/api/ratings/${ratingId}/respond`, {
      response_text: responseText,
    });
    return data;
  } catch (error) {
    handleApiError(error, "Failed to respond to rating");
  }
};

export const flagRating = async (ratingId) => {
  try {
    const data = await httpClient.post(`/api/ratings/${ratingId}/flag`);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to flag rating");
  }
};

export const getMyGivenRatings = async () => {
  try {
    const data = await httpClient.get("/api/ratings/my/given");
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch your ratings");
  }
};

export const getMyReceivedRatings = async () => {
  try {
    const data = await httpClient.get("/api/ratings/my/received");
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch received ratings");
  }
};

export const deleteRating = async (ratingId) => {
  try {
    await httpClient.delete(`/api/ratings/${ratingId}`);
    return { success: true };
  } catch (error) {
    handleApiError(error, "Failed to delete rating");
  }
};

// ============================================================================
// LEGACY EXPORTS (for backward compatibility during migration)
// ============================================================================

export const fetchUserData = getUserProfile;

// Export all functions
export default {
  // Auth
  signup,
  login,
  addRole,
  logout,
  resetPassword,
  getCurrentUser,
  getStoredToken,

  // Profile
  getUserProfile,
  updateUserProfile,
  getProfileStats,

  // Businesses
  getBusinesses,
  getBusinessById,
  getMyBusinesses,
  createBusiness,
  updateBusiness,
  deleteBusiness,
  searchBusinesses,

  // Earnings & Referrals
  getEarnings,
  getEarningsSummary,
  getTransactions,
  getReferrals,

  // Vouches
  getMyVouches,
  getBusinessVouches,

  // Badges
  getBadges,

  // Dashboard
  getAdvocateDashboard,
  getBusinessDashboard,

  // Notifications
  getNotifications,
  markNotificationRead,

  // Partnerships
  getPartnershipStatus,

  // Admin
  getPlatformSettings,
  updatePlatformSettings,
  getAdminAnalytics,

  // Ratings
  submitRating,
  getBusinessRatings,
  getVoucheeRatings,
  respondToRating,
  flagRating,
  getMyGivenRatings,
  getMyReceivedRatings,
  deleteRating,
};

const isInvoiceFallbackError = (error) => {
  const status = Number(error?.status);
  return status === 404 || status === 405;
};

const withInvoiceFallback = async (requestFns, fallbackMessage) => {
  let lastError = null;
  for (const requestFn of requestFns) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      if (isInvoiceFallbackError(error)) {
        continue;
      }
      throw error;
    }
  }

  if (lastError) {
    throw lastError;
  }
  throw new Error(fallbackMessage);
};

// Invoice and financing functions
export const createInvoice = async (invoiceData) => {
  const taxRate = Number.parseFloat(invoiceData.tax_rate) || 0;
  const backendPayload = {
    customer_name: invoiceData.customer_name,
    customer_email: invoiceData.customer_email,
    customer_address: invoiceData.customer_address || null,
    customer_phone: invoiceData.customer_phone || null,
    tax_rate: taxRate,
    discount: Number.parseFloat(invoiceData.discount) || 0,
    discount_type:
      invoiceData.discount_type || invoiceData.discountType || null,
    subtotal: Number.parseFloat(invoiceData.subtotal) || null,
    tax: Number.parseFloat(invoiceData.tax) || null,
    total: Number.parseFloat(invoiceData.total) || null,
    financing_enabled: Boolean(invoiceData.financing_enabled),
    financing_term: invoiceData.financing_term || null,
    items: (invoiceData.line_items || []).map((item) => ({
      description: item.description,
      quantity: Number.parseFloat(item.quantity) || 1,
      unit_price: Number.parseFloat(item.rate) || 0,
      tax_rate: taxRate,
    })),
    notes: invoiceData.notes || null,
    due_date: invoiceData.due_date || null,
    secure_id: invoiceData.secure_id || null,
    auto_add_contact: invoiceData.auto_add_contact !== false,
  };

  try {
    return await withInvoiceFallback(
      [() => httpClient.post("/api/invoicing/create", backendPayload)],
      "Failed to create invoice",
    );
  } catch (error) {
    handleApiError(error, "Failed to create invoice");
  }
};

export const calculateFinancing = async (amount, term) => {
  // Simple financing calculation - can be replaced with API call
  const interestRate = 0.05; // 5% interest
  const monthlyPayment = (amount * (1 + interestRate)) / term;
  return {
    monthlyPayment: monthlyPayment.toFixed(2),
    totalAmount: (monthlyPayment * term).toFixed(2),
    interestRate: (interestRate * 100).toFixed(1) + "%",
  };
};

// ============================================================================
// INVOICE MANAGEMENT - List, Resend, Record Payment, Delete
// ============================================================================

export const listInvoices = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset) params.append("offset", filters.offset);

    const queryString = params.toString();
    const listPath = `/api/invoicing/list${queryString ? `?${queryString}` : ""}`;

    const data = await withInvoiceFallback(
      [() => httpClient.get(listPath)],
      "Failed to fetch invoices",
    );
    return Array.isArray(data) ? data : data?.invoices || [];
  } catch (error) {
    console.error("Failed to fetch invoices:", error);
    return [];
  }
};

export const getInvoiceById = async (invoiceId) => {
  try {
    const data = await withInvoiceFallback(
      [() => httpClient.get(`/api/invoicing/${invoiceId}`)],
      "Failed to fetch invoice details",
    );
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch invoice details");
  }
};

export const resendInvoice = async (invoiceId) => {
  try {
    const data = await withInvoiceFallback(
      [() => httpClient.post(`/api/invoicing/${invoiceId}/send`)],
      "Failed to resend invoice",
    );
    return data;
  } catch (error) {
    handleApiError(error, "Failed to resend invoice");
  }
};

export const recordInvoicePayment = async (invoiceId, paymentData = {}) => {
  try {
    const data = await withInvoiceFallback(
      [
        () =>
          httpClient.post(
            `/api/invoicing/${invoiceId}/record-payment`,
            paymentData,
          ),
      ],
      "Failed to record payment",
    );
    return data;
  } catch (error) {
    handleApiError(error, "Failed to record payment");
  }
};

export const deleteInvoice = async (invoiceId) => {
  try {
    await withInvoiceFallback(
      [() => httpClient.delete(`/api/invoicing/${invoiceId}`)],
      "Failed to delete invoice",
    );
    return { success: true };
  } catch (error) {
    handleApiError(error, "Failed to delete invoice");
  }
};

export const createInvoiceCheckoutSession = async (invoiceId, payload = {}) => {
  try {
    const data = await withInvoiceFallback(
      [
        () =>
          httpClient.post(`/api/invoicing/${invoiceId}/payment-link`, payload),
      ],
      "Failed to start Stripe Checkout",
    );
    return data;
  } catch (error) {
    handleApiError(error, "Failed to start Stripe Checkout");
  }
};

export const downloadInvoicePdf = async (invoiceId) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const headers = {
    Accept: "application/pdf,application/octet-stream",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const candidates = [`/api/invoicing/${invoiceId}/pdf`];

  let lastError = null;

  for (const path of candidates) {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, { headers });
      if (!response.ok) {
        if (response.status === 404 || response.status === 405) {
          lastError = new Error("Invoice PDF endpoint not found");
          continue;
        }
        let message = "Failed to download invoice PDF";
        try {
          const body = await response.json();
          message = body?.detail || body?.message || message;
        } catch {
          // ignore body parsing
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = match
        ? decodeURIComponent(match[1].replace(/"/g, ""))
        : `invoice-${invoiceId}.pdf`;

      return { blob, filename };
    } catch (error) {
      lastError = error;
      const status = Number(error?.status);
      if (status === 404 || status === 405) {
        continue;
      }
    }
  }

  if (lastError) {
    throw lastError;
  }
  throw new Error("Failed to download invoice PDF");
};
